import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';

interface PhotoLightboxProps {
  photos: string[];
  startIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Full-screen image viewer wrapping yet-another-react-lightbox. The library gives us
// industry-standard behavior for free: touch swipe, pinch zoom, wheel zoom + pan,
// keyboard navigation, focus trap, ESC-to-close, lazy loading, and accessible
// counter/close-button affordances.
//
// API kept identical to the prior hand-rolled lightbox so existing callers don't change.
export function PhotoLightbox({ photos, startIndex = 0, open, onOpenChange }: PhotoLightboxProps) {
  if (photos.length === 0) return null;

  return (
    <Lightbox
      // Re-mount on each set of images so YARL's internal slide index is fresh.
      // Without this, opening signatures right after photos can leave the library
      // pointing at a stale slide index that's out of bounds for the new array.
      key={`${photos.length}:${photos[0] ?? ''}:${startIndex}`}
      open={open}
      close={() => onOpenChange(false)}
      slides={photos.map(src => ({ src }))}
      index={startIndex}
      plugins={[Zoom, Counter]}
      // Bump z-index above the Route Tracker's WorkSummaryDrawer (z-[9999]) so the
      // lightbox sits on top when launched from inside the drawer. White-background
      // rule for transparent-PNG signatures lives in src/index.css (.yarl__slide_image)
      // — keeps zoom/pan/swipe intact (overriding render.slide would break the Zoom plugin).
      styles={{ root: { zIndex: 100000 } }}
      // Zoom defaults: double-click to zoom 2x, wheel to zoom, pinch on touch.
      // 5x native pixel density helps inspect signature/photo detail.
      zoom={{
        maxZoomPixelRatio: 5,
        scrollToZoom: true // wheel/trackpad zoom without holding modifier keys
      }}
      counter={{
        container: {
          style: { top: 'unset', bottom: 16, left: '50%', transform: 'translateX(-50%)' }
        }
      }}
      // Disable carousel infinite loop when there's only one image — small UX polish.
      carousel={{ finite: photos.length <= 1 }}
      controller={{ closeOnBackdropClick: true }}
      animation={{ swipe: 250, fade: 200, zoom: 200 }}
    />
  );
}
